import type { ComponentType } from "react";
import { createBrowserRouter, Outlet } from "react-router";
import { AppLayout } from "./app/AppLayout";
import { ConfirmProvider } from "./app/confirm";
import { RouteError } from "./app/RouteError";
import { loginLoader, panelLoader } from "./app/session";
import { SummaryPage } from "./pages/SummaryPage";

/**
 * Each section's code is fetched the first time it is opened, not with the panel: on a
 * phone's data the first visit only downloads the shell and the summary. The summary
 * stays in the main bundle because it is where everybody lands.
 */
const page = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) => ({
  lazy: async () => ({ Component: (await load())[name] }),
});

/**
 * Every section of the panel under its own address.
 *
 * The addresses are the panel's real state: reloading, sharing a link or pressing "back"
 * lands exactly where one was — the open conversation, the filtered log, the rule being
 * edited — instead of always on the summary.
 *
 * The server knows these same paths (`src/http/plugins/spa.ts`) to answer them with the
 * panel; a section added here has to be added there too.
 */
export const router = createBrowserRouter([
  {
    path: "/login",
    loader: loginLoader,
    ...page(() => import("./pages/LoginPage"), "LoginPage"),
    errorElement: <RouteError />,
  },
  {
    path: "/",
    loader: panelLoader,
    element: (
      <ConfirmProvider>
        <Outlet />
      </ConfirmProvider>
    ),
    errorElement: <RouteError />,
    children: [
      { path: "conexion", ...page(() => import("./pages/SetupPage"), "SetupPage") },
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <SummaryPage /> },
          {
            path: "publicaciones",
            ...page(() => import("./pages/PostsPage"), "PostsPage"),
            children: [
              { path: ":mediaId", ...page(() => import("./features/insights/PostDetailRoute"), "PostDetailRoute") },
            ],
          },
          {
            path: "automatizaciones",
            ...page(() => import("./pages/AutomationsPage"), "AutomationsPage"),
            children: [
              { path: "nueva", ...page(() => import("./features/rule-editor/RuleEditorRoute"), "RuleEditorRoute") },
              { path: ":ruleId", ...page(() => import("./features/rule-editor/RuleEditorRoute"), "RuleEditorRoute") },
            ],
          },
          {
            path: "conversaciones/:conversationId?",
            ...page(() => import("./pages/ConversationsPage"), "ConversationsPage"),
          },
          { path: "personas", ...page(() => import("./pages/ContactsPage"), "ContactsPage") },
          { path: "actividad", ...page(() => import("./pages/ActivityPage"), "ActivityPage") },
          { path: "ajustes", ...page(() => import("./pages/SettingsPage"), "SettingsPage") },
          { path: "*", ...page(() => import("./pages/NotFoundPage"), "NotFoundPage") },
        ],
      },
    ],
  },
]);
